import type Luminary from '@lumen5/luminary';
import type { Scene } from '@lumen5/luminary';
import {
    VideoData,
    armMatchCutDuplicateRealignment,
    duplicateMatchCutAssets,
    rehydrateMatchCutDuplicates,
} from '@lumen5/luminary';

import { NONE_TRANSITION_URL } from 'js/src/components/app/transitions-sidebar/transition-data';
import { setSceneTransitionOperation } from 'js/src/components/app/transitions-sidebar/transition-operations';
import { checkpointVideo, checkpointVideoOperation } from 'js/src/data/video-data';
import uuid from 'js/src/libs/uuid';
import { saveRawLuminary } from 'js/src/luminary/index';
import {
    createLuminaryOperation,
    type LuminaryOperationContext,
} from 'js/src/luminary/luminary-hooks';
import {
    getAdjacentMorphDisqualifiedReason,
    hasAppliedMatchCut,
} from 'js/src/luminary/luminary-selectors';
import { getAnimatedZoomGapState } from 'js/src/luminary/morph-utils';
import type { MorphRecommendation } from 'js/src/luminary/morph-recommendation';
import { disqualifyMorph, scoreScenePairForMorph } from 'js/src/luminary/morph-recommendation';

export const toggleSceneMorphEnabledOperation = createLuminaryOperation(
    (
        { luminary, dispatch }: LuminaryOperationContext<Luminary>,
        sceneId: string,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        const scene = luminary.getSceneById(sceneId);

        if (!scene) {
            return;
        }
        luminary.batchRun(() => {
            scene.morphEnabled = !scene.morphEnabled;
            if (!scene.morphEnabled) {
                // Leave no trace of a match-cut, so a reload or re-enable can't replay a stale one.
                scene.matchCutTechnique = VideoData.MATCH_CUT_NONE;
                clearMatchCutArtifacts(scene, scene.nextScene);
            }
        });
        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );
    },
);

// Duplicates are excluded from save but live in-session. Only the scene's own originals have their
// morph props reset — nextScene only ever receives duplicates, never modified originals.
function clearMatchCutArtifacts(scene: Scene, nextScene: Scene | undefined): void {
    [scene, nextScene].forEach((target) => {
        if (!target) return;
        target.stickers
            .getAssets({ deep: false })
            .filter((sticker) => sticker.isMatchCutDuplicate)
            .forEach((duplicate) => {
                target.stickers.removeChild(duplicate);
                duplicate.destroy(true);
            });
    });

    scene.getAssets({ deep: true }).forEach((asset) => {
        asset.morphAlphaOnly = false;
        asset.morphAlphaStep = false;
        asset.morphInterpolation = VideoData.LINEAR_INTERPOLATION_PRESET;
    });

    // The morph-prop reset above only touches `scene`, but the freeze sets `isMatchCutFrozen` on assets
    // across BOTH scenes — so clear it on both here.
    [scene, nextScene].forEach((target) => {
        if (!target) return;
        target.getAssets({ deep: true }).forEach((asset) => {
            asset.isMatchCutFrozen = false;
        });
    });
}

export const applyRecommendedMorphOperation = createLuminaryOperation(
    ({ luminary }, sceneId: string): MorphRecommendation | null => {
        const scene = luminary.getSceneById(sceneId);
        const nextScene = scene?.nextScene;
        if (!scene || !nextScene) {
            return null;
        }

        if (scene.isPrePackagedMorphWithNextScene) {
            return disqualifyMorph('scene-sequence');
        }

        const recommendation = scoreScenePairForMorph(scene, nextScene);
        // Keyed on an applied match cut rather than the raw flag: editing either side of a gap can strand
        // `morphEnabled` on a pair that morphs nothing, and that gap still has a match cut to gain.
        if (!recommendation.recommended || hasAppliedMatchCut(scene)) {
            return recommendation;
        }

        // A 3+-scene morph chain is out of scope, and the toggle-off cleanup
        // (clearMatchCutArtifacts) only untangles a single pair.
        const adjacentMorphReason = getAdjacentMorphDisqualifiedReason(scene);
        if (adjacentMorphReason) {
            return disqualifyMorph(adjacentMorphReason);
        }

        luminary.batchRun(() => {
            duplicateMatchCutAssets(scene, nextScene, recommendation.pairings);
            scene.morphEnabled = true;
            scene.matchCutTechnique = recommendation.techniqueId;
        });
        armMatchCutDuplicateRealignment(luminary);
        return recommendation;
    },
);

export const applyMatchCutOperation = createLuminaryOperation(
    (
        { luminary, call }: LuminaryOperationContext<Luminary>,
        sceneId: string,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ): MorphRecommendation | null => {
        const nextSceneId = luminary.getSceneById(sceneId)?.nextScene?.id;
        if (!nextSceneId) {
            return null;
        }
        const recommendation = luminary.batchRun((safeLuminary): MorphRecommendation | null => {
            const applied = call(applyRecommendedMorphOperation, sceneId, {
                luminary: safeLuminary,
            });
            if (!applied?.recommended) {
                return applied;
            }
            call(setSceneTransitionOperation, nextSceneId, NONE_TRANSITION_URL, {
                luminary: safeLuminary,
            });
            return applied;
        });
        if (recommendation?.recommended) {
            call(checkpointVideoOperation, { systemTriggered, parentActionGroup });
        }
        return recommendation;
    },
);

export const syncMatchCutsWithTransitionsEnabledOperation = createLuminaryOperation(
    (
        { luminary, dispatch }: LuminaryOperationContext<Luminary>,
        transitionsEnabled: boolean,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        const matchCutScenes = luminary.scenes.filter(
            (scene) => scene.matchCutTechnique !== VideoData.MATCH_CUT_NONE,
        );
        if (matchCutScenes.length === 0) {
            return;
        }

        luminary.batchRun((safeLuminary) => {
            matchCutScenes.forEach((scene) => {
                if (transitionsEnabled) {
                    scene.morphEnabled = true;
                } else if (scene.morphEnabled) {
                    scene.morphEnabled = false;
                    clearMatchCutArtifacts(scene, scene.nextScene);
                }
            });
            if (transitionsEnabled) {
                rehydrateMatchCutDuplicates(safeLuminary);
            }
        });
        if (transitionsEnabled) {
            armMatchCutDuplicateRealignment(luminary);
        }

        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );
    },
);

/**
 * Turns the gap into `sceneId` into an animated zoom: the previous scene morphs into this one, and
 * the gap's own transition is cleared so the two can't both play. Batched under one checkpoint so a
 * single undo reverts both halves.
 */
export const applyAnimatedZoomTransitionOperation = createLuminaryOperation(
    (
        { luminary, call }: LuminaryOperationContext<Luminary>,
        sceneId: string,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ): boolean => {
        const scene = luminary.getSceneById(sceneId);
        if (!scene) {
            return false;
        }
        const { zoomSceneId, isAvailable } = getAnimatedZoomGapState(scene);
        if (!zoomSceneId || !isAvailable) {
            return false;
        }

        luminary.batchRun((safeLuminary) => {
            const zoomScene = safeLuminary.getSceneById(zoomSceneId);
            if (zoomScene) {
                zoomScene.morphEnabled = true;
                zoomScene.getAssets({ deep: true }).forEach((asset) => {
                    asset.morphInterpolation = VideoData.EASE_IN_OUT_INTERPOLATION_PRESET;
                });
            }
            call(setSceneTransitionOperation, sceneId, NONE_TRANSITION_URL, {
                luminary: safeLuminary,
            });
        });
        call(checkpointVideoOperation, { systemTriggered, parentActionGroup });
        return true;
    },
);
